import { Outlet } from "react-router-dom";
import styled from "styled-components";

import { useEffect, useRef } from "react";
import { VersionTag } from "../../core/components/VersionTag";
import { getChainConfigLoaded } from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import {
  IPAD_MINI_BREAKPOINT,
  IPAD_MINI_BREAKPOINT_NUMBER,
} from "../../core/styles/breakpoints";
import { useHandleParamNavigation } from "../../core/utils/useHandleParamNavigation";
import useWindowDimensions from "../../core/utils/useWindowDimensions";
import {
  resolveStepAfterPracticeSelection,
  usePracticeSelectionSkip,
} from "../appointment/utils/appointmentFlow";
import { useBookingTracking } from "../appointment/utils/useBookingTracking";
import { useWidgetConfig } from "../../widget/WidgetContext";
import { Header } from "./components/Header";
import { SideBar } from "./components/SideBar";
import { useScrollToTop } from "./utils/useScrollToTop";

export const AppointmentLayout = () => {
  useScrollToTop(false);
  useBookingTracking();
  const { isWidget } = useWidgetConfig();
  const navigate = useHandleParamNavigation();
  const chainConfigLoaded = useMainStore(getChainConfigLoaded);
  const setCurrentPracticeId = useMainStore(
    (state) => state.setCurrentPracticeId,
  );
  const { isSkipped, practiceId, bookablePractices } =
    usePracticeSelectionSkip();
  const entryResolved = useRef(false);

  // PF-5480: which screen the flow starts on depends on the chain's data, so decide only once chainConfig
  // has arrived — and only once per mount. The store is persisted, so a currentPracticeId left over from an
  // earlier visit must never skip the selection for a chain that has several bookable practices.
  useEffect(() => {
    if (entryResolved.current || !chainConfigLoaded) return;
    entryResolved.current = true;

    if (bookablePractices.length === 0) {
      navigate("/appointment/appointmentUnavailable", { replace: true });
    } else if (isSkipped && practiceId) {
      setCurrentPracticeId(practiceId);
      const targetStep = resolveStepAfterPracticeSelection();
      navigate(`/appointment/${targetStep}`, { replace: true });
    } else {
      navigate("/appointment/practice", { replace: true });
    }
  }, [
    chainConfigLoaded,
    bookablePractices,
    isSkipped,
    practiceId,
    navigate,
    setCurrentPracticeId,
  ]);

  const width = useWindowDimensions().width;
  const isDesktop = width >= IPAD_MINI_BREAKPOINT_NUMBER;
  const OutletContainerComponent = isWidget
    ? OutletContainerWidget
    : OutletContainer;
  const showSidebar = isDesktop && !isWidget;

  // Render nothing rather than a loader: LoadingScreen is a 100vh absolute overlay and would break out of
  // the widget's floating container, and a spinner has no readable colour on this two-tone background.
  if (!chainConfigLoaded) return null;

  return (
    <>
      {!isWidget && <Header />}
      <Container $isWidget={isWidget}>
        <ScrollContainer $isWidget={isWidget} className="scroll-ancestor">
          {showSidebar && <SideBar />}
          {(!isDesktop || isWidget) && <BackgroundDiv $isWidget={isWidget} />}
          <OutletContainerComponent>
            {/* <MainButton text="Reset State" onClick={resetState} /> */}
            <Outlet />
            <VersionTag />
          </OutletContainerComponent>
        </ScrollContainer>
      </Container>
    </>
  );
};

const Container = styled.div<{ $isWidget: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  min-height: ${({ $isWidget }) => ($isWidget ? "auto" : "100vh")};
  min-height: ${({ $isWidget }) => ($isWidget ? "auto" : "100dvh")};
  height: ${({ $isWidget }) => ($isWidget ? "100%" : "100vh")};
  height: ${({ $isWidget }) => ($isWidget ? "100%" : "100dvh")};
  overflow: hidden;
`;

const ScrollContainer = styled.div<{ $isWidget: boolean }>`
  overflow-y: auto;
  position: relative;
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  ${(props) => `
    @media (min-width: ${props.$isWidget ? "10000000px" : IPAD_MINI_BREAKPOINT}) {
      padding-left: calc(30vw + 20px);
      align-items: flex-start;
    }
  `}
`;

const OutletContainer = styled.div`
  margin-top: 90px;
  padding-left: 20px;
  padding-right: 20px;
  max-width: 600px;
  width: 100%;
  padding-bottom: 200px;
  margin-top: 70px;
  z-index: 1;
`;

const OutletContainerWidget = styled.div`
  position: relative;
  padding: 20px;
  max-width: 100%;
  width: 100%;
  padding-bottom: 40px;
  box-sizing: border-box;
  z-index: 1;
`;

const BackgroundDiv = styled.div<{ $isWidget: boolean }>`
  position: ${(props) => (props.$isWidget ? "absolute" : "fixed")};
  top: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background-color: ${({ theme }) => theme.colors.dark};
  z-index: 0;
`;
